import { inject, Injectable } from '@angular/core';
import { Commands } from '@shared/enums/commands.enum';
import {
  BrandingData,
  FeedbackSettings,
  InviteData,
  SmsSenderRestrictions,
  TeamAdminSettings,
  TeamAuthSettings,
  TeamData,
  TeamParentSettings,
  TeamType,
  UserData,
} from '@shared/models/account.model';
import { FeatureValue, TeamFeature } from '@shared/models/features.model';
import { mapListKeys } from '@shared/operators/map-list-keys.operator';
import { mapObjectKey } from '@shared/operators/map-object-key.operator';
import { shareRef } from '@shared/operators/share-ref.operator';
import { AuditLogService } from '@shared/services/audit-log.service';
import { CloudFunctions } from '@shared/services/cloud-functions.service';
import { DatabaseWrapper } from '@shared/services/database-wrapper.service';
import { firebaseSafe } from '@shared/utilities/object.utilities';
import { parseDefaultAdminSettings, parseDefaultParentSettings } from '@shared/utilities/team.utilities';
import { combineLatest, forkJoin, from, Observable, of, tap } from 'rxjs';
import { catchError, distinctUntilChanged, map, retry, shareReplay, switchMap } from 'rxjs/operators';
import { serverTimestamp } from 'firebase/database';
import { ZefApi } from '@shared/services/zef-api.service';
import { AuthManager } from '@shared/services/auth-manager.service';

@Injectable({
  providedIn: 'root',
})
export class AccountManager {
  private db = inject(DatabaseWrapper);
  private cf = inject(CloudFunctions);
  private al = inject(AuditLogService);
  private api = inject(ZefApi);
  private auth = inject(AuthManager);

  listUserInvites(): Observable<InviteData[]> {
    return this.auth.currentUser().pipe(
      map((user) => user?.email),
      distinctUntilChanged(),
      switchMap((email) =>
        email
          ? this.db
              .list<InviteData[]>(`/invites`, (ref) => ref.orderByChild('email').equalTo(email))
              .snapshotChanges()
              .pipe(
                retry(),
                catchError(() => of([])),
                mapListKeys(),
              )
          : of([]),
      ),
    );
  }

  defaultTeamKey(userKey: string): Observable<string> {
    return this.db
      .list(`/users/${userKey}/teams`, (ref) => ref.orderByValue().equalTo(5).limitToFirst(1))
      .snapshotChanges()
      .pipe(map((teams) => teams?.[0]?.key));
  }

  public loadUser(userKey: string): Observable<UserData> {
    return !userKey
      ? of(null)
      : this.db
          .object<UserData>(`/users/${userKey}/`)
          .snapshotChanges()
          .pipe(
            catchError(() => of(null)),
            mapObjectKey(),
            shareReplay({ refCount: true, bufferSize: 1 }),
          );
  }

  public loadTeam(teamKey: string): Observable<TeamData | null> {
    if (!teamKey) return of(null);

    return this.fetchBaseTeamData(teamKey).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),

      tap(([adminSettings]) => this.logAuditIfNeeded(teamKey, adminSettings)),

      switchMap(([adminSettings, team]) =>
        combineLatest([this.fetchParentSettings(teamKey, adminSettings), this.fetchAdminFeedback(adminSettings)]).pipe(
          map(([parentSettings, adminFeedback]) =>
            this.assembleTeamObject(teamKey, team, adminSettings, parentSettings, adminFeedback),
          ),
        ),
      ),
      catchError(() => of(null)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  // Helper method for `loadTeam`. Fetches the base team data including admin settings and team data.
  private fetchBaseTeamData(teamKey: string): Observable<[TeamAdminSettings | null, TeamData | null]> {
    return combineLatest([
      this.db
        .object<TeamAdminSettings>(`/admin/teams/${teamKey}`)
        .valueChanges()
        .pipe(
          map((settings) => parseDefaultAdminSettings(settings)),
          catchError(() => of(null)),
        ),
      this.db
        .object<TeamData>(`/teams/${teamKey}`)
        .valueChanges()
        .pipe(catchError(() => of(null))),
    ]);
  }

  // Helper method for `loadTeam`. Logs audit data if audit logging is enabled for the team.
  private logAuditIfNeeded(teamKey: string, settings: TeamAdminSettings | null): void {
    if (settings?.auditLogging) {
      this.al.queueDataLog({ status: 'SUCCESS', access: 'READ', path: `/teams/${teamKey}` });
    }
  }

  // Helper method for `loadTeam`. Fetches the parent settings of the team.
  private fetchParentSettings(
    teamKey: string,
    settings: TeamAdminSettings | null,
  ): Observable<TeamParentSettings | null> {
    const isParentOrChild = [TeamType.Parent, TeamType.Child].includes(settings?.type);
    if (!isParentOrChild) return of(null);

    const parentKey = settings?.childOf || teamKey;
    return this.db
      .object<TeamParentSettings>(`/admin/parent/${parentKey}`)
      .valueChanges()
      .pipe(catchError(() => of(null)));
  }

  // Helper method for `loadTeam`. Fetches the admin feedback settings of the team.
  private fetchAdminFeedback(settings: TeamAdminSettings | null): Observable<Record<string, FeedbackSettings> | null> {
    if (settings?.type === TeamType.Child) return of(null);

    return this.db
      .object<Record<string, FeedbackSettings>>(`/admin/core/feedback`)
      .valueChanges()
      .pipe(catchError(() => of(null)));
  }

  // Helper method for `loadTeam`. Assembles the final team object with all settings.
  private assembleTeamObject(
    teamKey: string,
    team: TeamData | null,
    adminSettings: TeamAdminSettings | null,
    parentSettings: TeamParentSettings | null,
    adminFeedback: Record<string, FeedbackSettings> | null,
  ): TeamData {
    const isChildTeam = adminSettings?.type === TeamType.Child;
    const feedback = isChildTeam ? parentSettings?.feedback : adminFeedback || undefined;
    const mergedParentSettings =
      parentSettings || feedback ? parseDefaultParentSettings({ ...parentSettings, feedback }) : null;
    return {
      ...team,
      authSettings: team?.authSettings,
      adminSettings,
      parentSettings: mergedParentSettings,
      $key: teamKey,
    } as TeamData;
  }

  public switchTeam(userKey: string, teamKey: string) {
    return !userKey || !teamKey
      ? of(null)
      : forkJoin([
          this.db.object(`/users/${userKey}/identities/${teamKey}/lastLogin`).set(Date.now()),
          this.db.object(`/users/${userKey}/team`).set(teamKey),
        ]);
  }

  public acceptTeamInvite(teamKey: string, inviteKey: string) {
    return this.cf.post(Commands.InviteUser, `${teamKey}/${inviteKey}`);
  }

  public rejectTeamInvite(inviteKey: string) {
    return this.cf.delete(Commands.InviteUser, inviteKey);
  }

  public updateLastActivityTime(userKey: string) {
    this.db.object(`/auth/login/${userKey}/lastActivity`).set(serverTimestamp());
  }

  updateTeam(teamKey: string, newData: Partial<TeamData>): Observable<unknown> {
    return from(this.db.object<TeamData>(`/teams/${teamKey}`).update(newData));
  }

  updateTeamParentSettings(teamKey: string, update: Partial<TeamParentSettings>): Observable<any> {
    return from(this.db.object(`/admin/parent/${teamKey}`).update(update));
  }

  updateTeamParentFeedback(teamKey: string, updateKey: string, update: Partial<FeedbackSettings>): Observable<any> {
    updateKey = updateKey || this.db.createPushId();
    return from(this.db.object(`/admin/parent/${teamKey}/feedback`).update({ [updateKey]: firebaseSafe(update) }));
  }

  updateAdminFeedback(updateKey: string, update: Partial<FeedbackSettings>): Observable<any> {
    updateKey = updateKey || this.db.createPushId();
    return from(this.db.object(`/admin/core/feedback`).update({ [updateKey]: firebaseSafe(update) }));
  }

  loadAdminFeedback(): Observable<FeedbackSettings[]> {
    return this.db
      .object<Record<string, FeedbackSettings>>(`/admin/core/feedback`)
      .valueChanges()
      .pipe(
        map((feedback) => Object.entries(feedback || {}).map(([key, value]) => ({ ...value, $key: key }))),
        catchError(() => of([])),
        shareRef(),
      );
  }

  loadParentFeedback(teamKey: string): Observable<FeedbackSettings[]> {
    if (!teamKey) return of([]);

    return this.db
      .object<Record<string, FeedbackSettings>>(`/admin/parent/${teamKey}/feedback`)
      .valueChanges()
      .pipe(
        map((feedback) => Object.entries(feedback || {}).map(([key, value]) => ({ ...value, $key: key }))),
        catchError(() => of([])),
        shareRef(),
      );
  }

  updateTeamParentBranding(teamKey: string, update: Partial<BrandingData>): Observable<any> {
    return from(this.db.object(`/admin/parent/${teamKey}/branding`).set(update));
  }

  updateTeamFeature(teamKey: string, feature: TeamFeature, value: FeatureValue) {
    const data = value || null;

    return from(this.db.object(`/admin/teams/${teamKey}/features/${feature}`).set(data));
  }

  toggleAdminTools(userKey: string, value: boolean) {
    return from(this.db.object(`/users/${userKey}/hideAdminTools`).set(value || null));
  }

  updateTeamFeatures(teamKey: string, data: Record<TeamFeature, boolean>) {
    return from(this.db.object(`/admin/teams/${teamKey}/features`).update(data));
  }

  updateTeamAuth(teamKey: string, data: TeamAuthSettings) {
    return from(this.db.object(`/teams/${teamKey}/authSettings`).update(data));
  }

  saveSMSCountryRestrictions(
    teamKey: string,
    restrictions: SmsSenderRestrictions,
    isConsole?: boolean,
  ): Observable<void> {
    return this.api.post<void>(`${isConsole ? 'admin' : 'organization'}/smsSender/${teamKey}`, { restrictions });
  }
}
